import { when } from 'mobx'
import { api } from 'store/api'
import { controller } from 'store/controller'
import { ExistingStore } from 'store/generation/ExistingStore'
import { GeneratingStore } from 'store/generation/GeneratingStore'
import { InferenceStore, NewInferenceData } from 'store/inference/InferenceStore'
import { ItemStore } from 'store/item/ItemStore'
import { me } from 'store/me'
import { opt, STAND_BANNER } from 'store/opt'
import ProfileStore from 'store/profile/ProfileStore'
import { stat } from 'store/stat'
import { store } from 'store/store'
import { fromItem, fromOfferItem, GenerateRequest, InferenceResponse, ItemData } from 'type/Inference'
import { OfferItem } from 'type/rec'
import { mix } from 'util/array'
import { postMessage, sendEvent } from 'util/message'
import { delay } from 'util/promise'
import { parseUrl } from 'util/url'

export type GenerateParams = {
  single?: ItemStore
  rec?: OfferItem
  source?: InferenceStore
}

function getItemImages(item: ItemStore) {
  const { selected_item_images, item_full_image, item_combo_image } = item.json
  if (selected_item_images?.length) return selected_item_images
  if (item_full_image) return [item_full_image]
  if (item_combo_image) return [item_combo_image]
  return []
}

function getInferenceImages(inference: InferenceStore) {
  const { selected_item_images } = inference
  if (selected_item_images?.length) return selected_item_images
  const { item_full_image, item_combo_image } = inference.items[0] ?? {}
  if (item_full_image) return [item_full_image]
  if (item_combo_image) return [item_combo_image]
  return []
}

function getRecImages(rec: OfferItem) {
  const { selected_item_images, item_full_image, item_combo_image } = rec
  if (selected_item_images?.length) return selected_item_images
  if (item_full_image) return [item_full_image]
  if (item_combo_image) return [item_combo_image]
  return []
}

function getBaseSelectedImages(params: GenerateParams): string[] {
  const { single, source } = params
  if (single) return getItemImages(single)
  if (source) return getInferenceImages(source)
  if (store.item) return getItemImages(store.item)
  return []
}

function getComboSelectedImages(params: GenerateParams): string[] {
  const { single, rec } = params
  if (single) return []
  if (rec) return getRecImages(rec)
  if (store.comboItem) return getItemImages(store.comboItem)
  return []
}

function getSelectedImages(params: GenerateParams): string[] {
  const base = getBaseSelectedImages(params)
  const combo = getComboSelectedImages(params)
  return mix(base, combo)
}

function sendGenStat(request: GenerateRequest, data: NewInferenceData, response: InferenceResponse, combo_cart: boolean) {
  const { item_id, combo_item_id, identity_id } = request
  const { item, identity_mode } = data
  const { collection } = item
  const { inference_id } = response
  const source_inference_id = data.source?.inference_id
  stat.gen(item_id, collection, identity_id, inference_id, identity_mode, combo_item_id, source_inference_id, combo_cart)
}

function sameGen(inference: InferenceStore, item: ItemData, combo: ItemData | undefined, identity_id: string) {
  return inference.item_id === item.item
    && inference.items[1]?.item === combo?.item
    && inference.identity_id === identity_id
}

function samePics(profile: ProfileStore, inference: InferenceStore) {
  const a = parseUrl(profile.common.pic)
  const b = parseUrl(inference.pic)
  return a?.pathname === b?.pathname
}

export async function generateLook(params: GenerateParams = {}) {
  const profile = store.profile
  if (!profile) throw new Error('no profile')

  const open_item_id = store.open_item_id
  if (!store.available) throw new Error('no limit')

  const { rec, source } = params
  const single: ItemData | undefined = fromItem(params?.single?.json) ?? source?.items[0]
  const item: ItemData | undefined = single ?? fromItem(store.item?.json)
  const combo: ItemData | undefined = single ? undefined : rec ? fromOfferItem(rec) : fromItem(store.comboItem?.json)
  const combo_item_id = combo?.external_item_id || combo?.item
  const combo_cart = !rec && !!combo_item_id
  const video = !!source

  if (!item) throw new Error('no item')

  const identity_id = profile.identity_id
  const identity_mode = profile.form.mode
  const pic = profile.common.pic
  const selected_item_images = getSelectedImages(params)
  const data: NewInferenceData = { identity_id, identity_mode, pic, selected_item_images, item, combo, source }

  if (opt.single_gen) {
    const inference = store.inferences.inferences.find(it => sameGen(it, item, combo, identity_id))
    if (inference && samePics(profile, inference)) {
      store.existing = new ExistingStore(inference)
      store.screen = 'existing'
      store.state = 'max'
      return
    }
  }

  async function go() {
    const BANNER_TIMEOUT = 15_000
    const start = Date.now()
    const gen = new GeneratingStore(video)

    try {
      store.generating = gen
      store.screen = 'generating'
      store.inferences.tab = opt.tsum ? 'new' : 'all'

      const inference = InferenceStore.fromEmpty()
      inference.beforeCreate(data)

      store.generating.inference = inference
      store.inferences.addNewInference(inference)

      const createInference = async () => {
        let request: GenerateRequest | undefined
        try {
          await profile.ensureSaved()

          request = await buildGenerateRequest(combo_item_id)

          postMessage('on-generation-start', {
            item_id: request.item_id,
            combo_item_id: request.combo_item_id,
            video,
          })

          const response = video
            ? await api.generateVideo(source.inference_id)
            : await api.generateInference(request)

          sendGenStat(request, data, response, combo_cart)
          inference.afterCreate(response)
          sendEvent('on-create-outfit', {
            item_id: request.item_id,
            combo_item_id: request.combo_item_id,
            video,
          })

          postMessage('on-generation-start-success', {
            item_id: request.item_id,
            combo_item_id: request.combo_item_id,
            video,
            inference_id: response.inference_id,
          })
        } catch (e) {
          inference.onError()

          postMessage('on-generation-start-error', {
            item_id: request?.item_id,
            combo_item_id: request?.combo_item_id,
            video,
            error: e instanceof Error ? e.message : String(e),
          })
          throw e
        }
      }

      const creating = createInference()

      if (opt.stand && STAND_BANNER) {
        await creating
        await when(() => inference.complete)
        await delay(start + BANNER_TIMEOUT - Date.now())
      }

      controller.openInference(inference, 'new')
      store.checkLimit()
      store.showUserDataBannerMaybe()

      await creating
    } catch (e) {
      console.error('generation failed', e)
      gen.error = true

      if (!store.generating?.inference) {
        postMessage('on-generation-start-error', {
          video,
          error: e instanceof Error ? e.message : String(e),
        })
      }
    }
  }

  function needCombineFull(combo_item_id: string | undefined) {
    if (combo_item_id) return false
    return opt.combine_full && ['full', 'simple'].includes(profile.type)
  }

  async function buildGenerateRequest(combo_item_id?: string): Promise<GenerateRequest> {
    const { user_id, manager_id } = me
    const user_email = me.email
    const user_ext_id = me.external_user
    const identity_id = profile.identity_id
    const vendor = opt.options.vendor
    const gemini_model = opt.options.gemini_model
    const combo_generation = needCombineFull(combo_item_id) ? true : undefined
    const high = store.tg

    const item_ext_id = item?.external_item_id
    const ext = !!item_ext_id && item_ext_id === open_item_id
    const item_id = ext ? item_ext_id : item?.item

    if (!identity_id) throw new Error('no identity')
    if (!item_id) throw new Error('no item')

    return {
      user_id,
      manager_id,
      item_id,
      combo_item_id,
      identity_id,
      country_code: 'RU',
      user_email,
      user_ext_id,
      item_ext_id,
      high,
      vendor,
      gemini_model,
      combo_generation,
    }
  }

  await go()
}
